import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { Box, BoxProps, CardMedia, CardMediaProps, Skeleton } from '@mui/material';

import { useDebounce } from 'usehooks-ts';

import { getCDNOptimizedImage, getPropertyPrimaryPhoto } from '@arrived/common';
import { Offering } from '@arrived/models';

export interface PropertyGridCardThumbnailProps extends Omit<BoxProps, 'children'> {
  offering: Offering;
  cardMediaProps?: Omit<CardMediaProps<'img'>, 'component' | 'image' | 'ref'>;
}

export const PropertyGridCardThumbnail = ({ cardMediaProps, offering, ...rest }: PropertyGridCardThumbnailProps) => {
  const [isImageLoading, setIsImageLoading] = useState(true);
  const { ref, width } = useResizeDetector({ handleWidth: true });

  const imageWidth = useDebounce(width ?? 100, 250);

  const { onLoad, sx, ...cardMediaRest } = cardMediaProps || {};

  const imageSrc = useMemo(() => {
    const { properties } = offering;

    return getCDNOptimizedImage({
      imgUrl: getPropertyPrimaryPhoto(properties[0]),
      ratio: '5:3',
      width: `${imageWidth * 2}px`,
    });
  }, [offering, imageWidth]);

  const handleOnLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      setIsImageLoading(false);
      onLoad?.(event);
    },
    [onLoad]
  );

  return (
    <Box width="100%" position="relative" ref={ref} {...rest}>
      <CardMedia
        component="img"
        image={imageSrc}
        onLoad={handleOnLoad}
        sx={{
          objectFit: 'cover',
          visibility: isImageLoading ? 'hidden' : 'visible',
          aspectRatio: '5 / 3',
          ...sx,
        }}
        {...cardMediaRest}
      />
      <Skeleton
        component="div"
        variant="rectangular"
        sx={{
          display: isImageLoading ? 'block' : 'none',
          zIndex: 2,
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
        }}
      />
    </Box>
  );
};
